import { useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './auth';
import { LoginPage } from './pages/LoginPage';
import { AppLayout } from './pages/AppLayout';
import { DashboardPage } from './pages/DashboardPage';
import { RequestDetailPage } from './pages/RequestDetailPage';
import { NewRequestPage } from './pages/NewRequestPage';
import { AdminPage } from './pages/AdminPage';
import { ensurePushSubscription } from './lib/push';
import { getSocket } from './lib/socket';
import { InstallPrompt } from './components/InstallPrompt';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, refetchOnWindowFocus: false },
  },
});

function ProtectedShell() {
  const { me, loading } = useAuth();

  useEffect(() => {
    if (me) {
      // Open the WS connection.
      getSocket();
      // Auto-register push ONLY if permission already granted (no prompt
      // here — iOS rejects requestPermission outside a user gesture). When
      // permission is 'default'/'denied', the PushPermissionBanner in the
      // dashboard will prompt the user with a tap.
      if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        void ensurePushSubscription();
      }
    }
  }, [me]);

  if (loading) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center text-slate-400">
        Cargando…
      </div>
    );
  }
  if (!me) return <Navigate to="/login" replace />;
  return <AppLayout />;
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route element={<ProtectedShell />}>
              <Route path="/" element={<DashboardPage />} />
              <Route path="/new" element={<NewRequestPage />} />
              <Route path="/request/:id" element={<RequestDetailPage />} />
              <Route path="/admin" element={<AdminPage />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Routes>
          <InstallPrompt />
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  );
}
